import type { KeyboardEvent } from 'react';
import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { CloseIcon, colors } from '@truvhq/ui';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { MOBILE_BREAKPOINT } from 'shared/lib/screen/useScreenSize';

import ArrowUpIcon from './icons/arrow-up.svg?react';

type ChatComposerProps = {
  isStreaming: boolean;
  /** Text to open with, so an unsent message survives the composer being unmounted. */
  draft: string;
  onSend: (text: string) => void;
  onStop: () => void;
  onDraftChange: (draft: string) => void;
};

/**
 * Take focus on mount unless the page already has someone typing.
 *
 * Opening the assistant is a decision to type in it, so the field should be ready. What it must not
 * do is pull the caret out of a field the page autofocused — the test is whether focus is in an
 * editable element, not merely whether something has it: the launcher that opened this panel still
 * holds focus at this point, and that is no reason to leave the composer cold.
 */
function useFocusOnMount() {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const active = document.activeElement;
    const isSomeoneTyping =
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      (active instanceof HTMLElement && active.isContentEditable);
    if (!isSomeoneTyping) {
      inputRef.current?.focus();
    }
  }, []);

  return inputRef;
}

// The theme greys out every adornment icon button from `.MuiOutlinedInput-root
// .MuiInputAdornment-root`, which out-specifies an sx on the button — so the colour goes on the
// icon, where nothing can inherit over it. `hover` isn't on the MUI palette, only in `colors`.
const sendButtonSx = (isArmed: boolean) =>
  ({
    flex: 'none',
    width: 32,
    height: 32,
    borderRadius: '8px',
    backgroundColor: isArmed ? colors.primary.main : 'transparent',
    '& svg': { color: isArmed ? colors.white : colors.grey60 },
    '&:hover': { backgroundColor: isArmed ? colors.primary.hover : colors.grey20 },
  }) as const;

// trim().min(1) gates empty / whitespace-only input.
const schema = z.object({ message: z.string().trim().min(1) });
type FormValues = z.infer<typeof schema>;

export function ChatComposer({ isStreaming, draft, onSend, onStop, onDraftChange }: ChatComposerProps) {
  const inputRef = useFocusOnMount();
  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { message: draft },
  });
  // Read the field rather than formState.isValid, which stays false until the first change event and
  // would leave the button disabled over a restored draft.
  const isArmed = useWatch({ control, name: 'message' }).trim().length > 0;

  const submit = handleSubmit(({ message }) => {
    onSend(message.trim());
    reset({ message: '' });
    onDraftChange('');
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Enter sends; Shift+Enter inserts a newline. The Send button is the discoverable path.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Controller
        name="message"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            maxRows={6}
            size="small"
            placeholder="Ask Truv AI…"
            onChange={(event) => {
              field.onChange(event);
              onDraftChange(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            // Background, placeholder colour and the focused outline are the theme's, so this field
            // looks like every other field in the dashboard. Only what the send button needs is set
            // here: room for it on the right, and a line box that doesn't tower over it.
            sx={{
              '& .MuiOutlinedInput-root': {
                alignItems: 'center',
                gap: '8px',
                padding: '10px 8px 10px 16px',
              },
              '& .MuiInputBase-input': {
                padding: '3px 0',
                lineHeight: '20px',
                // iOS zooms the page in on a field under 16px, and the sheet is the whole screen on
                // a phone — so the zoom takes the send button off the edge of it.
                [`@media (max-width: ${MOBILE_BREAKPOINT - 1}px)`]: { fontSize: '1rem', lineHeight: '22px' },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ margin: 0, height: 'auto' }}>
                  {isStreaming ? (
                    <IconButton onClick={onStop} aria-label="Stop generating" sx={sendButtonSx(false)}>
                      <CloseIcon width={16} height={16} />
                    </IconButton>
                  ) : (
                    // Fills in once there's something to send, so the button reads as armed.
                    <IconButton type="submit" disabled={!isArmed} aria-label="Send message" sx={sendButtonSx(isArmed)}>
                      <ArrowUpIcon width={20} height={20} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </form>
  );
}
